import useModal from '@/hooks/useModal/useModal';
import { useQueryParams } from '@/hooks/useQueryParams/useQueryParams';
import CourseStageBox from '@/uikit/components/CourseStageBox/CourseStageBox';
import Stack from '@/uikit/components/Stack/Stack';
import HeroArea from '@/uikit/composites/HeroArea/HeroArea';
import { HeroSize } from '@/uikit/composites/HeroArea/consts';
import CourseContactFormModal from '@uikit/composites/CourseContactFormModal/CourseContactFormModal';
import getBaseUrl from '@uikit/helpers/getBaseUrl';
import { Align } from '@uikit/types';
import { useMemo, useState } from 'react';
import Button from '../Button/Button';
import { ButtonAlign, ShowIcon } from '../Button/consts';
import ButtonLink from '../ButtonLink/ButtonLink';
import { ClickableAction } from '../Clickable/consts';
import ContactTelephone from '../ContactTelephone/ContactTelephone';
import Container from '../Container/Container';
import { ContainerChildrenSpace, ContainerSize } from '../Container/consts';
import CourseDetailCardList from '../CourseDetailCardList/CourseDetailCardList';
import CourseTimetable from '../CourseTimetable/CourseTimetable';
import Headline from '../Headline/Headline';
import { HeadlineAlign, HeadlineColor } from '../Headline/consts';
import Icon from '../Icon/Icon';
import RichText from '../RichText/RichText';
import Section from '../Section/Section';
import { SectionBackgroundColor, SectionVariant } from '../Section/consts';
import Spacer from '../Spacer/Spacer';
import CourseDetailRequestFormModal from './CourseDetailRequestFormModal';
import CourseRegistrationFormModal from './CourseRegistrationFormModal';
import { createdurationInfoFromEvents as createdurationInfo } from './helpers';
import type { CourseDetailPageContentProps } from './types';

const EVENT_SECTION_ID = 'events-section';
const REQUEST_FORM_MODAL_ID = 'course-request-form';
const CONTACT_FORM_MODAL_ID = 'course-contact-form';
const REGISTER_FORM_MODAL_ID_PREFIX = 'course-registration-form-';

const baseUrl = getBaseUrl();

const CourseDetailPageContent = ({
  category,
  contactFormApiTarget,
  contactMailTemplateId,
  contactRecipientId,
  dailyPlans,
  description,
  events,
  image,
  location,
  price,
  registrationFormApiTarget,
  registrationFormMailTemplateId,
  registrationFormRecipientId,
  targetGroup,
  title,
  path,
  durationInfo,
}: CourseDetailPageContentProps) => {
  const { showModal } = useModal();

  const {
    params: { 'show-modal': modalID },
  } = useQueryParams();

  const [currentEventID, setCurrentEventID] = useState(() =>
    typeof modalID === 'string'
      ? modalID.replace(REGISTER_FORM_MODAL_ID_PREFIX, '')
      : ''
  );

  const currentEvent = useMemo(
    () => events?.find(({ id }) => id === currentEventID),
    [currentEventID, events]
  );

  const courseLink = [baseUrl, path].join('');

  const registrationModalId = `${REGISTER_FORM_MODAL_ID_PREFIX}${currentEventID}`;

  const hasEvents = !!events && events.length > 0;

  const handleCourseSubmit = (eventID: string) => {
    setCurrentEventID(eventID);
    showModal(`${REGISTER_FORM_MODAL_ID_PREFIX}${eventID}`);
  };

  return (
    <>
      <HeroArea
        image={image}
        size={HeroSize.Large}
        skewedBottom={true}
        hasAspectRatioFallback={true}
      >
        <CourseStageBox
          title={title}
          category={category.title}
          location={location}
          durationInfo={
            durationInfo || createdurationInfo(events, dailyPlans.length)
          }
          euroPrice={price}
          anchorID={EVENT_SECTION_ID}
        />
      </HeroArea>

      {targetGroup && (
        <Section variant={SectionVariant.LargePaddingBottom}>
          <Container>
            <Stack flexDirection="row" gap="1rem" wrap>
              <Stack.Item flex="4 1 300px">
                <Headline headlineColor="gradient" align={HeadlineAlign.Left}>
                  Für wen ist der Kurs geeignet?
                </Headline>
              </Stack.Item>
              <Stack.Item flex="8 1 450px">
                <RichText>{targetGroup}</RichText>
              </Stack.Item>
            </Stack>
          </Container>
        </Section>
      )}

      <Section
        bgColor={
          targetGroup
            ? SectionBackgroundColor.SurrogateLight
            : SectionBackgroundColor.Default
        }
        variant={SectionVariant.LargePadding}
      >
        <Container spaceBetweenChildren={ContainerChildrenSpace.Small}>
          <Headline
            headlineColor={HeadlineColor.Gradient}
            subText={description}
            align={HeadlineAlign.Left}
          >
            Kursinhalte
          </Headline>

          <CourseDetailCardList items={dailyPlans} />
        </Container>
      </Section>

      <Section
        sectionId={EVENT_SECTION_ID}
        bgColor={SectionBackgroundColor.White}
        variant={SectionVariant.LargePadding}
      >
        <Container
          size={ContainerSize.Small}
          spaceBetweenChildren={ContainerChildrenSpace.Small}
        >
          <Headline
            headlineColor="gradient"
            align={HeadlineAlign.Center}
            subText={
              hasEvents &&
              'Wählen Sie einen passenden Zeitraum für Ihre Schulung aus und melden Sie sich über unser Online-Formular ganz bequem an.'
            }
          >
            Termine & Anmeldung
          </Headline>

          {hasEvents ? (
            <CourseTimetable events={events} onSubmit={handleCourseSubmit}>
              <ButtonLink
                align={ButtonAlign.Center}
                actionType={ClickableAction.Modal}
                modallink="course-request-form"
                disableTransition
              >
                Individuellen Termin anfragen
              </ButtonLink>
            </CourseTimetable>
          ) : (
            <Stack alignItems="center" gap="1rem">
              <Icon
                variant="user/contact-person"
                size="iconSize64"
                isOutlined
              />
              <RichText size="medium" align="center">
                Für diese Schulung planen wir Termine nur auf Anfrage.
              </RichText>
              <Spacer defaultSize={1} sm={2} />
              <Button
                align={ButtonAlign.Center}
                actionType={ClickableAction.Modal}
                modallink="course-request-form"
              >
                Individuellen Termin anfragen
              </Button>
            </Stack>
          )}
        </Container>
      </Section>

      <Section
        bgColor={SectionBackgroundColor.SurrogateLight}
        variant={SectionVariant.LargePadding}
      >
        <Container
          align={Align.Center}
          spaceBetweenChildren={ContainerChildrenSpace.Mini}
          size={ContainerSize.Small}
        >
          <Stack gap="2rem">
            <Headline
              headlineColor={HeadlineColor.Dark}
              align={HeadlineAlign.Center}
              level={3}
            >
              Haben Sie Fragen zu unseren Schulungsthemen? Oder gibt es weitere
              Themen zu denen Sie eine Schulung wünschen?
            </Headline>

            <Button
              actionType={ClickableAction.Modal}
              modallink={CONTACT_FORM_MODAL_ID}
              align={ButtonAlign.Center}
              icon="user/speechbubble"
              showIcon={ShowIcon.ShowIcon}
            >
              Jetzt Kontakt aufnehmen!
            </Button>
          </Stack>

          <Stack gap="1.25rem">
            <Headline
              headlineColor={HeadlineColor.Dark}
              align={HeadlineAlign.Center}
              level={4}
              tag="h5"
            >
              oder rufen Sie uns an!
            </Headline>

            <ContactTelephone phone="0511.430.3452" />
          </Stack>
        </Container>
      </Section>

      <CourseRegistrationFormModal
        id={registrationModalId}
        categoryTitle={category.title}
        courseLink={courseLink}
        currentEvent={currentEvent}
        formApiTarget={registrationFormApiTarget}
        mailTemplateId={registrationFormMailTemplateId}
        price={price}
        recipientId={registrationFormRecipientId}
        title={title}
      />

      <CourseContactFormModal
        id={CONTACT_FORM_MODAL_ID}
        categoryTitle={category.title}
        courseTitle={title}
        formApiTarget={contactFormApiTarget}
        link={courseLink}
        mailTemplateId={contactMailTemplateId}
        recipientId={contactRecipientId}
      />

      <CourseDetailRequestFormModal
        id={REQUEST_FORM_MODAL_ID}
        categoryTitle={category.title}
        formApiTarget={contactFormApiTarget}
        mailTemplateId={contactMailTemplateId}
        recipientId={contactRecipientId}
        courseLink={courseLink}
        title={title}
      />
    </>
  );
};

export default CourseDetailPageContent;
